import { useCallback, useEffect, useMemo, useState } from 'react';
import { DossierFilter } from '../../../model';
import {
  dossierSliceActions,
  getDossierFilter,
  selectDossierState,
} from '../../../store/data/dossier';
import {
  getCurrentState,
  getSelectedWalletSection,
  myWalletSliceActions,
  retrieveWallet,
} from '../../../store/features/wallet';
import { useAppDispatch, useAppSelector } from '../../redux';

export interface MyWalletDossierFilterLogic {
  flagOpenDossierFilter: boolean;
  dossierFilter: DossierFilter;
  setFlagOpenDossierFilter: (value: boolean) => void;
  onDossierFilterChange: (code: string, value: boolean) => void;
  onSubmit: () => void;
  onCancel: () => void;
}

export const useMyWalletDossierFilterLogic = (): MyWalletDossierFilterLogic => {
  const dispatch = useAppDispatch();

  const { defaultDossier, dossierFilter: appliedFilter } =
    useAppSelector(selectDossierState);

  const [localFilter, setLocalFilter] = useState(null);

  const [flagOpenDossierFilter, setFlagOpenDossierFilter] = useState(false);

  const selectedWalletSection: any = useAppSelector(getSelectedWalletSection);
  const { myWalletSection }: any = useAppSelector(getCurrentState);

  const dossierBySection = useMemo(
    () => getDossierFilter(localFilter, selectedWalletSection),
    [localFilter, selectedWalletSection]
  );

  useEffect(() => {
    if (defaultDossier) {
      if (appliedFilter) {
        const merged: DossierFilter = {
          ...defaultDossier,
          dossier: defaultDossier.dossier?.map((d) => {
            const appliedD = appliedFilter.dossier?.find(
              (ad) => ad.code === d.code
            );
            return {
              ...d,
              flagSelected: !!appliedD?.flagSelected,
              subDossier: d.subDossier?.map((sd) => ({
                ...sd,
                flagSelected: !!appliedD?.subDossier?.some(
                  (asd) => asd.contract === sd.contract
                ),
              })),
            };
          }),
        };
        setLocalFilter(merged);
      } else {
        setLocalFilter(defaultDossier);
      }
    }
  }, [defaultDossier, appliedFilter]);

  const onDossierFilterChange = useCallback(
    (code: string, value: boolean) => {
      const clone: DossierFilter = JSON.parse(JSON.stringify(localFilter));

      clone.dossier?.some((dossier) => {
        const isChecked = dossier.code === code;
        if (isChecked) {
          dossier.flagSelected = value;
          dossier.subDossier?.forEach(
            (subDossier) => (subDossier.flagSelected = value)
          );
        }
        return (
          isChecked ||
          dossier.subDossier?.some((subDossier) => {
            const isSubChecked = subDossier.contract === code;

            if (isSubChecked) {
              subDossier.flagSelected = value;
              dossier.flagSelected =
                dossier.subDossier.length ===
                dossier.subDossier.filter((sub) => sub.flagSelected).length;
            }

            return isSubChecked;
          })
        );
      });

      setLocalFilter(clone);
    },
    [localFilter, setLocalFilter]
  );

  const onSubmit = useCallback(() => {
    const filteredLocalFilter = {
      ...localFilter,
      dossier: localFilter.dossier
        .map((dossier) => ({
          ...dossier,
          subDossier:
            dossier.subDossier?.filter((sub) => sub.flagSelected) || [],
        }))
        .filter(
          (dossier) => dossier.flagSelected || dossier.subDossier.length > 0
        ),
    };

    dispatch(dossierSliceActions.setDossierFilter(filteredLocalFilter));
    dispatch(retrieveWallet());
    if (myWalletSection === 'PERFORMANCE') {
      dispatch(myWalletSliceActions.setMyWalletSection('POSITION_LIST'));
    }
    setFlagOpenDossierFilter(false);
  }, [dispatch, localFilter, myWalletSection]);

  const onCancel = useCallback(() => {
    if (defaultDossier?.dossier?.length > 0) {
      const allSelectedFilter = {
        dossier: defaultDossier.dossier.map((d) => ({
          ...d,
          flagSelected: true,
          subDossier: d.subDossier.map((s) => ({ ...s, flagSelected: true })),
        })),
      };
      setLocalFilter(allSelectedFilter);
      dispatch(dossierSliceActions.setDossierFilter(allSelectedFilter));
      dispatch(retrieveWallet());
      if (myWalletSection === 'PERFORMANCE') {
        dispatch(myWalletSliceActions.setMyWalletSection('POSITION_LIST'));
      }
    }
    setFlagOpenDossierFilter(false);
  }, [defaultDossier, dispatch, myWalletSection]);

  return {
    flagOpenDossierFilter,
    dossierFilter: dossierBySection,
    onDossierFilterChange,
    setFlagOpenDossierFilter,
    onSubmit,
    onCancel,
  };
};
